import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "new_components/atoms/Button";

// Section components (each should accept an onSelect prop and a selectedValue prop)
import ReportOptions from "./sections/ReportOptions";
import ReportCategories from "./sections/ReportCategories";
import ReportPrimaries from "./sections/ReportPrimaries";
import ROIReportOptions from "./sections/ROIReportOptions";
import LongFormAnchorFilters from "./sections/LongFormAnchorFilters";
// Request components
import ReportPermissions from "./sections/ReportPermissions";
import ReportRequest from "./sections/ReportRequest";
import ROIReportRequest from "./sections/ROIReportRequest";
import LongFormRequest from "./sections/LongFormRequest";

// Separated initial step for report selection
import SelectReportTemplateStep from "./ReportSelection";

// Template integration
import ActionMenuLayout from "new_components/templates/SubContentTemplate";

// Actions and helpers
import {
  SG_GET_DEBRIEF_PDF_REPORT_TEMPLATES,
  SG_GET_EMPLOYEE_USER_CATEGORIES,
  SG_CREATE_DEBRIEF_PDF_REPORT,
} from "constants/actions";
import { filter_data } from "reports/Tag/calculations";
import { get_ROI_params } from "app/audit/simple/paramCalculation";
import { v4 as uuidv4 } from "uuid";
import { getReportLink } from "../utils";
import { calculateTableData } from "./sections/calculations";

const PDFReportCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [reportType, setReportType] = useState(12);
  const [reportConfig, setReportConfig] = useState(null);
  const [reportOptions, setReportOptions] = useState(null);
  const [reportCategories, setReportCategories] = useState([]);
  const [reportPrimaries, setReportPrimaries] = useState([]);
  const [reportPrimariesSelected, setReportPrimariesSelected] = useState(false);
  const [secondarySelected, setSecondarySelected] = useState(false);
  const [anchorName, setAnchorName] = useState("");
  const [reportSettings, setReportSettings] = useState(null);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [orgName, setOrgName] = useState("");
  const [roiParams, setRoiParams] = useState(null);
  const [notEnoughData, setNotEnoughData] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [prevDataCalculated, setPrevDataCalculated] = useState(null);
  const [anchorSelected, setAnchorSelected] = useState(null);
  const [useLatestData, setUseLatestData] = useState(false);
  const [useLastData, setUseLastData] = useState(false);

  // Redux state
  const {
    get_employee_categories,
    get_auth_employee,
    latestData,
    get_auth,
    coreData,
    lastData,
    anchor,
    WhiteLabel
  } = useSelector(
    (state) => ({
      get_auth_employee: state.auth,
      coreData: state.audit?.core_data,
      pdfReports: state.pdfReports,
      get_auth: state.auth,
      latestData: state.audit?.filtered_data,
      get_employee_categories: state.employee_category,
      lastData: state.audit?.filtered_last_data,
      anchor: state.audit?.anchor,
      WhiteLabel: state.white_label?.white_label,
    }),
    shallowEqual
  );

  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );


  useEffect(() => {
    // Check to see if the data is found in latestData[0]?.responses
    if (latestData?.[0]?.responses?.length > 0) {
      setUseLatestData(latestData[0]?.responses);
      return;
    }

    // Check if data is nested in [0][0] structure and normalize it
    if (latestData?.length > 0) {
      let filteredData;
      // Check if data is nested one level deeper than expected
      if (Array.isArray(latestData[0]) && latestData[0].length > 0) {

        setUseLatestData(latestData[0]); // Store normalized data structure
        if (filteredData?.length === 0) {
          setCalcResult(null);
          setPrevDataCalculated(null);
        }
      } else {
        setUseLatestData(latestData); // Use original structure
        if (filteredData?.length === 0) {
          setCalcResult(null);
          setPrevDataCalculated(null);
        }
      }

      // Do the same check for lastData if it exists
      if (lastData?.length > 0) {
        if (Array.isArray(lastData[0]) && lastData[0].length > 0) {
          setUseLastData(lastData[0]);
        } else {
          setUseLastData(lastData);
        }
      }
    }
  }, [latestData, lastData, anchor, selectedFilters]);
  const orgRule = !!WhiteLabel?.rule ? Number(WhiteLabel?.rule) : 3;

  // Perform calculation ONLY if data is ready and no previous calculation is running
  useEffect(() => {
    if (
      useLatestData?.length > 0 &&
      coreData &&
      anchor
    ) {

      const filteredDataLength = filter_data(useLatestData, selectedFilters)?.length;
      if (filteredDataLength < orgRule && currentStep > 1) {
        setNotEnoughData(true);
        return;
      } else {
        setNotEnoughData(false);
      }



      const calcResult = calculateTableData(
        useLatestData,
        selectedFilters,
        coreData.categories[0],
        anchor,
        orgRule || 3,
        coreData.questions,
        coreData.outcomeQuestions,
        coreData.standards?.response
      );

      setCalcResult(calcResult);

      if (useLastData?.length > 0) {
        const prevCalcResult = calculateTableData(
          useLastData,
          selectedFilters,
          coreData.categories[0],
          anchor,
          orgRule || 3,
          coreData.questions,
          coreData.outcomeQuestions
        );
        setPrevDataCalculated(prevCalcResult);
      }

      const anchorData = coreData.categories[0].categories.find(
        (dimension) => dimension.id === anchor
      );

      // If this is the primary anchor, clear the name to force the PDF to say "Overview"
      if (anchorData?.priority === "primary") {
        setAnchorName("PRIMARY_ANCHOR");
      } else {
        setAnchorName(anchorData?.name || "");
      }
    }
  }, [
    anchor,
    coreData,
    selectedFilters,
    useLatestData,
    useLastData,
  ]);

  // Fetch PDF report templates on mount
  useEffect(() => {
    dispatch({ type: SG_GET_DEBRIEF_PDF_REPORT_TEMPLATES });
  }, [dispatch]);

  // Set organization name based on employee categories or core data
  useEffect(() => {
    const employeeId = Number(get_auth_employee?.employee_id);
    const employeeCategories =
      get_employee_categories?.employee_user_category?.find(
        (category) => category.employee === employeeId
      );
    if (employeeCategories) {
      const orgNameVal = employeeCategories?.category?.find(
        (cat) => cat?.priority === "primary"
      )?.value;
      setOrgName(orgNameVal || coreData?.categories?.[0]?.name);
    } else {
      setOrgName(coreData?.categories?.[0]?.name);
    }
  }, [get_employee_categories, coreData, get_auth_employee]);

  // (Optional) Filtering logic for categories – if needed by your sections
  useEffect(() => {
    if (
      lastData?.length > 0 &&
      coreData?.categories?.[0]?.categories?.length > 0 &&
      reportCategories.length > 0 &&
      reportPrimaries.length > 0 &&
      reportOptions
    ) {
      const primary = coreData.categories[0].categories.find(
        (category) => category.priority === "primary"
      )?.id;
      const selectedFilters = createObjects(
        reportPrimaries,
        reportCategories,
        reportOptions,
        primary
      );
      let filteredData;
      if (lastData?.[0]?.[0]) {
        filteredData = filter_data(lastData[0], selectedFilters);
      } else {
        filteredData = filter_data(lastData, selectedFilters);
      }

      setNotEnoughData(filteredData.length === 0);
    }
    if (lastData?.length === 0 && reportType === 9) {
      setNotEnoughData(true);
    }
  }, [
    reportPrimaries,
    reportOptions,
    lastData,
    reportCategories,
    coreData,
    reportType,
    selectedFilters,
    ]);

  useEffect(() => {
    if (reportOptions && coreData?.categories?.length > 0) {
      const options = getCategoryOptions(
        coreData.categories[0].categories,
        reportOptions
      );
      const selectedFilters = options.map((option) => ({
        id: option.id,
        parent: reportOptions,
      }));
      let filteredLatestData;
      if (latestData?.[0]?.[0]) {
        filteredLatestData = filter_data(latestData[0], selectedFilters);
      } else {
        filteredLatestData = filter_data(latestData, selectedFilters);
      }

      const emptyFilters = getEmptyFilters(filteredLatestData, selectedFilters);
      const filteredOptions = options.filter(
        (option) => !emptyFilters.includes(option.id)
      );
      setFilteredCategories(filteredOptions);
    }
  }, [reportOptions, coreData, latestData]);

  useEffect(() => {
    if (reportOptions && coreData?.categories?.length > 0) {
      const secondaries = coreData.categories[0].categories.find(
        (category) => category.priority === "secondary"
      );
      const primary = coreData.categories[0].categories.find(
        (category) => category.priority === "primary"
      );
      const anchor = coreData.categories[0].categories.find(
        (category) => category.id === reportOptions
      );
      setSecondarySelected(
        secondaries?.id === reportOptions || primary?.id === reportOptions
      );
    }
  }, [reportOptions, coreData]);

  useEffect(() => {
    if (get_auth_employee?.employee_id) {
      const employeeId = Number(get_auth_employee.employee_id);
      dispatch({
        type: SG_GET_EMPLOYEE_USER_CATEGORIES,
        payload: `employee=${employeeId}`,
      });
    }
  }, [get_auth_employee, dispatch]);

  const reportSteps = {
    1: ["step1", "step2", "step3", "step4"],
    4: ["step1", "step4"],
    12: ["longformstep1", "longformstep2"],
  }


  // Define wizard steps – each section receives an onSelect to update state.
  const steps = [
    {
      id: "step1",
      categoryTitle: "Select Report Options",
      categoryContent:
        reportType === 8 ? (
          <ROIReportOptions
            onSelect={(value) => {
              if (!value) {
                setRoiParams(null);
                return;
              }
              const params = get_ROI_params(
                value?.id.toString(),
                value?.name,
                coreData,
                latestData?.[0]
              );
              setRoiParams(params);
            }}
            selectedCategory={reportOptions}
          />
        ) : (
          <ReportOptions
              onSelect={(value) => {
                setCurrentStep(1);
                
              if (!value) {
                setReportOptions(null);
                setSecondarySelected(false);
                return;
              }
                setReportOptions(value);
                
            }}
            selectedCategory={reportOptions}
          />
        ),
    },
    {
      id: "step2",
      categoryTitle: "Select Report Categories",
      categoryContent: (
        <ReportCategories
          onSelect={(value) => {
            setReportCategories(value);
          }}
          selectedCategory={reportCategories}
          parentCategory={reportOptions}
          filteredOptions={filteredCategories}
        />
      ),
    },
    !secondarySelected && {
      id: "step3",
      categoryTitle: "Select Report Primaries",
      categoryContent: (
        <ReportPrimaries
          onSelect={(value, selected) => {
            setReportPrimaries(value)
            // Used to handle cases where the user selects all primaries
            setReportPrimariesSelected(selected)
          }}
          selectedCategory={reportPrimaries}
          nomenclature={anchorName}
        />
      ),
    },
    {
      id: "step4",
      categoryTitle: "Report Request",
      categoryContent:
        reportType === 8 ? (
          <ROIReportRequest
            params={roiParams}
            templateId={reportType}
            get_auth={get_auth}
            orgName={orgName}
            onSelect={(value) => setReportSettings(value)}
          />
        ) : reportType === 4 || reportType === 7 ? (
          <ReportRequest
            primaries={reportPrimaries}
            categories={reportCategories}
            parentCategory={reportOptions}
            templateId={reportType}
            anchorName={anchorName}
            get_auth={get_auth}
            onSelect={(value) => setReportSettings(value)}
          />
        ) : (
          <ReportPermissions
            primaries={reportPrimaries}
            categories={reportCategories}
            templateId={reportType}
            parentCategory={reportOptions}
            anchorName={anchorName}
            get_auth={get_auth}
            onSelect={(value) => setReportSettings(value)}
            noData={reportType === 9 ? notEnoughData : false}
          />
        ),
    },
    {
      id: "longformstep1",
      categoryTitle: "Select Anchor",
      categoryContent: (
        <LongFormAnchorFilters
          setAnchorLabel={setAnchorName}
          setAnchorSelected={setAnchorSelected}
          anchorSelected={anchorSelected}
        />
      ),
    },
    {
      id: "longformstep2",
      categoryTitle: "Select Filters and Generate Report",
      categoryContent: (
        <LongFormRequest
          calculatedData={calcResult}
          prevDataCalculated={prevDataCalculated}
          anchorName={anchorName}
          orgName={orgName}
          get_auth={get_auth}
          anchor={anchor}
        />
      ),
    },
  ]?.filter((step) =>
    reportType ? reportSteps?.[reportType]?.includes(step?.id) : step
  );

  // If no report has been selected yet, show the selection component
  if (currentStep === 0) {
    return (
      <SelectReportTemplateStep
        backAction={() => navigate("/leader/audit/reports")}
        onSelect={({ reportType, config }) => {
          setReportType(reportType);
          setReportConfig(config);
          setCurrentStep(1);
        }}
      />
    );
  }

  const createLongFormReport = () => {
    const tracking_code = uuidv4();

    const report = {
      template_id: 12,
      organization: Number(get_auth?.organization_id),
      employee: Number(get_auth?.employee_id),
      tracking_code,
      params: {
        data: {
          calculatedData: calcResult,
          prevDataCalculated,
          anchorName: anchorName !== "PRIMARY_ANCHOR" ? anchorName : null,
          orgName,
        },
        report_link: getReportLink(12),
      },
    };

    dispatch({
      type: SG_CREATE_DEBRIEF_PDF_REPORT,
      payload: report,
    });

    navigate("/leader/audit/reports");
  };

  // API call integrated into handleNext:
  const createReport = () => {
    const uuid = uuidv4();

    if (reportType === 12) {
      createLongFormReport();
      return;
    }

    const params =
      reportType === 12
        ? { ...calcResult, anchorName, orgName, report_link: getReportLink(12) }
        : reportType === 8
        ? {
            ...roiParams,
            parent_id: null,
            parent_children: [],
            primary_list: [],
            report_link: getReportLink(reportType),
          }
        : {
            parent_id: reportOptions,
            parent_children: reportCategories,
            primary_list: reportPrimaries,
            pdfOptions: reportSettings,
            includeWarning: reportSettings?.includeWarning,
            orgName: orgName,
            report_link: getReportLink(reportType),
          };

    const report = {
      template_id: reportType,
      organization: Number(get_auth?.organization_id),
      employee: Number(get_auth?.employee_id),
      tracking_code: uuid,
      params,
    };

    dispatch({
      type: SG_CREATE_DEBRIEF_PDF_REPORT,
      payload: report,
    });

    if (reportConfig?.nomenclature === "Story") {
      navigate("/leader/audit/reports/stories");
    } else {
      navigate("/leader/audit/reports");
    }
  };

  // Updated handleNext function with final step logic
  const handleNext = () => {

    if (reportType === 1 && steps?.length === currentStep) {
      createReport();
      return;
    }

    // When on the final step, trigger the API call
    if (currentStep === reportSteps?.[reportType].length) {
      // Final step reached – create the report
      createReport();
      return;
    }

    if (reportType === 8 && currentStep === 2) {
      if (roiParams) {
        createReport();
        return;
      }
    }

    if (reportType === 12 && currentStep === 2) {
      if (calcResult) {
        createLongFormReport();
        return;
      }
    }

    // Advance wizard based on reportType
    switch (reportType) {
      case 8:
        setCurrentStep(currentStep + 1);
        break;
      case 4:
      case 7:
        if (currentStep === 2) {
          setCurrentStep(3);
        } else {
          setCurrentStep(currentStep + 1);
        }
        break;
      default:
        setCurrentStep(currentStep + 1);
        break;
    }
  };

  const isStepValid = (stepId) => {
    switch (stepId) {
      case "step1":
        return !!(reportOptions || roiParams);
      case "step2":
        return reportCategories.length > 0;
      case "step3":
        return secondarySelected || reportPrimaries.length > 0 || reportPrimariesSelected;
      case "step4":
        return true; // Always valid if reached
      case "longformstep1":
        return calcResult && anchorName && calcResult.responses.length > 0 && anchorSelected;
      case "longformstep2":
        return true; // Always valid if reached
      default:
        return false;
    }
  };
  
  const isNextButtonDisabled = (currentStepIndex) => {
    // Disable button if current step itself is invalid
    const currentStepId = steps[currentStepIndex]?.id;
    if (!isStepValid(currentStepId)) return true;
  
    // Additionally, ensure all previous steps are valid
    for (let i = 0; i < currentStepIndex; i++) {
      const stepId = steps[i].id;
      if (!isStepValid(stepId)) return true;
    }
  
    // All checks passed; enable the button
    return false;
  };

  const getButtonText = () => {
    if (currentStep < steps?.length || (reportType === 12 && currentStep === 1))
      return "Next";
    return "Generate Report";
  };

  const calculateMenuNavigationRules = () => {
    const rules = {};

    const stepCompletionChecks = {
      step1: () => reportOptions || roiParams,
      step2: () => reportCategories.length > 0,
      step3: () => secondarySelected || reportPrimaries.length > 0 || reportPrimariesSelected,
      step4: () => true, // Always enabled if previous steps are valid
      longformstep1: () => calcResult && anchorName && calcResult.responses.length > 0 && anchorSelected,
      longformstep2: () => true, // Always enabled if previous step is valid
    };
  
    // Iterate through the steps, marking each as enabled only if previous steps are valid
    let previousStepsValid = true;
    steps.forEach((step) => {
      const isCurrentStepValid = stepCompletionChecks[step.id] ? stepCompletionChecks[step.id]() : false;
      rules[step.id] = previousStepsValid; // Current step is enabled if all previous steps are valid
      previousStepsValid = previousStepsValid && isCurrentStepValid; // Update validity for next step
    });
  
    return rules;
  };

  return (
    <ActionMenuLayout
      heading="PDF Report Creation"
      pageTitle="Create PDF Report"
      type={"single"}
      menuItems={steps}
      onItemSelect={(item, i) => {
        const step = steps?.findIndex((step) => step.id === item.id)
        setCurrentStep(step + 1)
      }}
      menuNavigationRules={calculateMenuNavigationRules()}
      activeItem={steps[currentStep - 1]?.id}
      wizardMode={true}
      completedSteps={steps
        ?.filter((step, index) => index < currentStep - 1 && isStepValid(step.id))
        ?.map(step => step.id)}
      actionRowComponent={
        <div style={{ position: 'relative' }}>
          <Button
            disabled={isNextButtonDisabled(currentStep - 1) || notEnoughData}
            onClick={handleNext}
          >
            {getButtonText()}
          </Button>
          {(currentStep === steps?.length && reportType === 12 && calcResult?.responses?.length === 0 || notEnoughData) && (
            <div style={{
              color: 'red',
              position: 'absolute',
              top: '100%',
              right: '0',
              maxWidth: '400px',
              minWidth: '300px',
              textAlign: 'right',
              fontSize: '14px',
              lineHeight: '1.4',
              paddingTop: '8px'
            }}>
              Not enough data to generate a report, please ensure your selections have enough data.
            </div>
          )}
        </div>
      }
      backAction={() => navigate("/leader/audit/reports")}
    />
  );
};

export default PDFReportCreation;

/* Utility functions used in filtering – can be removed or moved elsewhere if not needed */
function createObjects(parent, ids, primary, primaryParent) {
  const result = [];
  if (primary && primary.length > 0) {
    result.push({
      fr: "",
      id: primary[0],
      name: "innerlogic1",
      color: "#ce7118",
      description: "",
      parent: primaryParent,
    });
  }
  ids.forEach((id) => {
    result.push({
      id,
      name: "Least Open",
      color: "blue",
      parent,
    });
  });
  return result;
}

function getCategoryOptions(categories, parentId) {
  let categoryList = [];
  const parentCategory = categories.find(
    (category) => category.id === parentId
  );
  if (parentCategory) {
    parentCategory.options.forEach((option) => {
      categoryList.push({
        id: option.id,
        name: option.name,
      });
    });
  }
  return categoryList;
}

function getEmptyFilters(data, filters) {
  let emptyFilters = [];
  filters?.forEach((filter) => {
    const hasData = data?.find((item) =>
      item?.categories?.find((cat) => cat?.response === filter?.id)
    );
    if (!hasData) {
      emptyFilters.push(filter.id);
    }
  });
  return emptyFilters;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
